import { z } from 'zod'
import { segnapostoMancanti } from '@praticheenergia/domain'
import { supabase } from '../lib/supabase'

export const SchemaModello = z
  .object({
    // Minuscole e trattini bassi: e' la chiave con cui il codice chiede il modello.
    chiave: z.string().trim().regex(/^[a-z0-9_]{3,40}$/, 'Chiave non valida (minuscole e trattini bassi)'),
    oggetto: z.string().trim().min(1, 'Oggetto obbligatorio'),
    corpo: z.string().trim().min(1, 'Corpo obbligatorio'),
    mittente: z
      .string()
      .trim()
      .transform((v) => (v === '' ? null : v))
      .refine((v) => v === null || /^[^@\s]+@[^@\s]+\.[^@\s]+$|^.+<[^@\s]+@[^@\s]+\.[^@\s]+>$/.test(v), 'Mittente non valido'),
    attivo: z.boolean(),
    // «gg/mm», vuota = nessuna data. Solo i modelli programmati la mostrano.
    dataInvio: z.string().trim().default(''),
  })
  // Un segnaposto inventato non da errore a nessuno: resta vuoto nell'email
  // che il cliente riceve. Meglio fermarlo qui.
  .refine((m) => segnapostoMancanti(`${m.oggetto} ${m.corpo}`).length === 0, {
    message: 'Segnaposto sconosciuto nel modello',
    path: ['corpo'],
  })
  .refine((m) => m.dataInvio === '' || leggiData(m.dataInvio) !== null, {
    message: 'Data di invio non valida (gg/mm, niente 29 febbraio)',
    path: ['dataInvio'],
  })
  .transform(({ dataInvio, ...m }) => {
    const data = dataInvio === '' ? null : leggiData(dataInvio)
    return { ...m, invioMese: data?.mese ?? null, invioGiorno: data?.giorno ?? null }
  })

// Stesso vincolo di 0045_email_programmate.sql: il 29 febbraio tre anni su
// quattro non esiste, e il job lo salterebbe.
const GIORNI_DEL_MESE = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

function leggiData(testo: string): { giorno: number; mese: number } | null {
  const m = /^(\d{1,2})\/(\d{1,2})$/.exec(testo)
  if (!m) return null
  const giorno = Number(m[1])
  const mese = Number(m[2])
  if (mese < 1 || mese > 12 || giorno < 1 || giorno > GIORNI_DEL_MESE[mese - 1]!) return null
  return { giorno, mese }
}

/** I modelli che il job giornaliero manda da solo (0045): solo su questi la
 * data di invio fa qualcosa. */
export function eProgrammato(chiave: string): boolean {
  return /^letture_(richiesta|sollecito)_/.test(chiave) || chiave === 'promemoria_31_12'
}

export const eSollecito = (chiave: string) => chiave.startsWith('letture_sollecito_')

export function formattaData(mese: number | null, giorno: number | null): string {
  return mese === null || giorno === null ? '' : `${String(giorno).padStart(2, '0')}/${String(mese).padStart(2, '0')}`
}

export type DatiModello = z.input<typeof SchemaModello>

export interface Modello {
  readonly id: number
  readonly chiave: string
  readonly oggetto: string
  readonly corpo: string
  readonly mittente: string | null
  readonly attivo: boolean
  readonly invioMese: number | null
  readonly invioGiorno: number | null
}

export async function elencaModelli(): Promise<Modello[]> {
  const { data, error } = await supabase
    .from('notifica_template')
    .select('id, chiave, oggetto, corpo, mittente, attivo, invio_mese, invio_giorno')
    .order('chiave')
  if (error) throw new Error(error.message)
  return ((data ?? []) as (Omit<Modello, 'invioMese' | 'invioGiorno'> & { invio_mese: number | null; invio_giorno: number | null })[]).map(
    ({ invio_mese, invio_giorno, ...m }) => ({ ...m, invioMese: invio_mese, invioGiorno: invio_giorno }),
  )
}

export async function salvaModello(id: number | null, dati: DatiModello): Promise<void> {
  const m = SchemaModello.parse(dati)
  const riga = {
    chiave: m.chiave,
    oggetto: m.oggetto,
    corpo: m.corpo,
    mittente: m.mittente,
    attivo: m.attivo,
    invio_mese: m.invioMese,
    invio_giorno: m.invioGiorno,
  }
  const { error } =
    id === null
      ? await supabase.from('notifica_template').insert(riga)
      : await supabase.from('notifica_template').update(riga).eq('id', id)
  if (error) {
    throw new Error(error.code === '23505' ? 'C e gia un modello con questa chiave' : error.message)
  }
}

export interface Invio {
  readonly id: number
  readonly chiave: string
  readonly destinatario: string
  readonly oggetto: string
  readonly stato: 'in_coda' | 'inviata' | 'errore'
  readonly errore: string | null
  readonly creatoIl: string
  readonly inviatoIl: string | null
}

/** Registro degli invii: lo scrive il worker, il portale lo legge soltanto. */
export async function elencaInvii(limite = 200): Promise<Invio[]> {
  const { data, error } = await supabase
    .from('notifica_inviata')
    .select('id, chiave, destinatario, oggetto, stato, errore, creato_il, inviato_il')
    .order('creato_il', { ascending: false })
    .limit(limite)
  if (error) throw new Error(error.message)
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: r.id as number,
    chiave: r.chiave as string,
    destinatario: r.destinatario as string,
    oggetto: r.oggetto as string,
    stato: r.stato as Invio['stato'],
    errore: r.errore as string | null,
    creatoIl: r.creato_il as string,
    inviatoIl: r.inviato_il as string | null,
  }))
}
